import deepmerge from "deepmerge";
import { useMemo } from "react";

import {
  ConfigurationDto,
  GetOrganizationResponseBodyType,
} from "@intelligems/ig-types";
import { useSelector } from "@src/store";
import {
  selectedViewAsMode,
  selectedViewAsProductId,
} from "@src/store/slices/appSettings";
import { useSuspenseQuery } from "@tanstack/react-query";

import { QueryMeta, SuspenseQueryOptions } from "../../types/react-query";
import { getAuthToken } from "../../utils";
import { message } from "../../utils/message";
import { api } from "../api";
import { organizationsKeys } from "../queryKeys";

const customArrayMerge = (
  target: ConfigurationDto[],
  source: ConfigurationDto[]
): ConfigurationDto[] => {
  // Create a new array to avoid mutating the target
  const result = [...target];

  // For each item in source array
  source.forEach((sourceItem) => {
    // Find matching item in the target array by key
    const targetIndex = result.findIndex(
      (targetItem) => targetItem.key === sourceItem.key
    );

    if (targetIndex !== -1) {
      // If a match is found, merge the objects and update the item in result
      result[targetIndex] = deepmerge(result[targetIndex], sourceItem);
    } else {
      // If no match is found, add the source item to the result
      result.push(sourceItem);
    }
  });

  return result;
};

const mergeConfigurations = (
  configArrays: ConfigurationDto[][]
): ConfigurationDto[] => {
  if (configArrays.length === 0) {
    return [];
  }
  if (configArrays.length === 1) {
    return configArrays[0];
  }

  // Use deepmerge with our custom array merge function
  return configArrays.reduce((merged, current) =>
    deepmerge(merged, current, { arrayMerge: customArrayMerge })
  );
};

export const getOrg = async (
  chargeId?: string,
  code?: string,
  viewAs?: boolean,
  viewAsProductId?: string
) => {
  const resp = await api.get<GetOrganizationResponseBodyType>(
    `/v2/organization`,
    {
      params: {
        chargeId: chargeId,
        code: code,
        viewAs:
          viewAs && viewAsProductId && viewAsProductId !== "live"
            ? viewAs
            : undefined,
        viewAsProductId:
          viewAs && viewAsProductId && viewAsProductId !== "live"
            ? viewAsProductId
            : undefined,
      },
    }
  );

  return resp.data;
};

export const useGetOrg = (
  options?: SuspenseQueryOptions<GetOrganizationResponseBodyType, Error>
) => {
  const viewAs = useSelector(selectedViewAsMode);
  const viewAsProductId = useSelector(selectedViewAsProductId);

  return useSuspenseQuery({
    ...options,
    queryKey: organizationsKeys.one,
    queryFn: async () => getOrg(undefined, undefined, viewAs, viewAsProductId),
    meta: {
      onSuccess: (data) => {
        message({
          type: "LOGGED_IN",
          payload: {
            id: data?.org?.id,
            name: data?.org?.name,
            shopId: data?.org?.shopId,
            token: getAuthToken(),
            installed: data?.org?.installed,
          },
        });
        const params = new URLSearchParams(window.location.search);
        if (params.has("widgetAuth")) {
          window.close();
        }
      },
    } as QueryMeta,
  });
};

export const useGetOrgId = (): string => {
  const org = useGetOrg();
  return useMemo(() => org?.data?.org?.id?.slice(-12), [org?.data?.org?.id]);
};

export const useOrgTimezone = (
  returnUtcDefault?: boolean
): string | undefined => {
  const org = useGetOrg();
  return org?.data?.org?.timezone ?? (returnUtcDefault ? "UTC" : undefined);
};
